import { datadogRum } from "@datadog/browser-rum";
import type { Location } from "history";
import { observe } from "mobx";
import type { UserConfigFragment } from "../app/core/generated/webapi.g";
import type { AdobeManager } from "../app/core/lib/adobe";
import type { Ads } from "../app/core/lib/ads";
import type { ComScoreManager } from "../app/core/lib/comScore";
import { triggerPixelPageView } from "../app/core/lib/facebook";
import { initGAConfig, updateGAPageView } from "../app/core/lib/googleAnalytics";
import { logger } from "../app/core/lib/logging";
import type { ParselyManager } from "../app/core/lib/parsely";
import { getMetadata } from "../app/core/lib/tagging";
import type { Store } from "./stores";

const log = logger.getChild("setupHistoryTracking");

type action = (location: Location<any>) => void;

export const setupHistoryTracking = ({
    store,
    adobe,
    parsely,
    comscore,
    ads,
}: {
    store: Store;
    adobe?: AdobeManager;
    parsely?: ParselyManager;
    comscore?: ComScoreManager;
    ads?: Ads;
}) => {
    const { history } = store;

    // These run on primary block change, which should be the same as route change, but gives you access to the block itself
    observe(
        store.block,
        "currentBlock",
        () => {
            // Rehydrate metadata
            getMetadata({ store });
            ads?.clear();
            // reverting this because it broke ads on content pages
            // App.ads?.cleanup(); // use this instead of clear() IHRAL-6705

            // Fire analytics calls
            adobe?.trackPageView();
            parsely?.trackPageView();
            comscore?.trackPageView();
        },
        true,
    );

    // These are only run on router navigation
    // NOTE: do not add getMetadata back in here. Duplicate calls means duplicate page views in Adobe.
    const historyListeners: action[] = [
        () => store.cache.clear(),
        () => (document.body.scrollTop = document.documentElement!.scrollTop = 0),
        () => store.storePage(null),
        () => {
            store.request.hostname = location.hostname;
            store.request.path = location.pathname;
            store.request.protocol = location.protocol;
            store.request.hash = location.hash;
            const query = {};
            if (location.search.length && location.search.includes("?")) {
                location.search
                    .split("?")[1]
                    .split("&")
                    .forEach(queryItem => {
                        const [key, value] = queryItem.split("=");
                        query[key] = value;
                    });
            }
            store.request.query = query;
        },
        () => {
            if (store.microsite && !location.pathname.includes("/featured/")) {
                store.clearMicrosite();
            }
        },
    ];

    if (history) {
        if (typeof window !== "undefined") {
            historyListeners.push(() => window.scrollTo(0, 0));
            historyListeners.push(() => triggerPixelPageView(store));
        }

        // Set up Google Analytics
        if (store.env.GOOGLE_ANALYTICS_TRACKING_ID) {
            initGAConfig(store.site.sections as UserConfigFragment, store.env.GOOGLE_ANALYTICS_TRACKING_ID);
            historyListeners.push(location => updateGAPageView(location.pathname));
        } else {
            log.debug("GOOGLE_ANALYTICS_TRACKING_ID not set, skipping Google tracking");
        }

        // Set up DataDog RUM
        if (store.env.DATADOG_APPLICATION_ID && store.env.DATADOG_CLIENT_ID) {
            const sampleRate = store.env.DATADOG_SAMPLE_RATE ? parseInt(store.env.DATADOG_SAMPLE_RATE, 10) : 100;
            datadogRum.init({
                applicationId: store.env.DATADOG_APPLICATION_ID,
                clientToken: store.env.DATADOG_CLIENT_ID,
                datacenter: "us",
                sampleRate,
            });

            if (store.env.DD_ENV) {
                datadogRum.addRumGlobalContext("ENVIRONMENT", store.env.DD_ENV);
            }
        } else {
            log.debug("DATADOG_APPLICATION_ID or DATADOG_CLIENT_ID not set, skipping Datadog tracking");
        }

        history.listen(location => historyListeners.forEach(listener => listener(location)));
    }
};
